import { db } from "../../../core/data/local/db";
import { loadReplica, LOYALTY_ENVELOPES, type LoyaltyReplica } from "./replica";
import type { StickersByLine } from "../domain/earn";
import { cardState, type CardState } from "../domain/balance";

/**
 * What this till has recorded for cards, in `moduleRecords` — the generic
 * per-module store core already provides.
 *
 * Two kinds, both written in the same transaction as their outbox envelope:
 *
 *   earn     one per sale with a card: the stickers it earned per line and
 *            the rewards it redeemed. Keyed by sale, so a void or refund can
 *            find what to give back, and "Add card" can see a sale already
 *            has one.
 *   reverse  each void or refund that gave something back.
 */

export const MODULE_ID = "loyalty";

export interface RedemptionRecord {
  id: string;
  saleLineId: string;
  menuItemId: string;
  kind: "drink" | "pastry";
  fullPrice: number;
}

export interface EarnRecord {
  kind: "earn";
  envelopeOpId: string;
  saleId: string;
  cardCode: string;
  digest: string;
  stickers: number;
  stickersByLine: StickersByLine;
  redemptions: RedemptionRecord[];
  createdAt: string;
}

export interface ReverseRecord {
  kind: "reverse";
  envelopeOpId: string;
  saleId: string;
  cardCode: string;
  digest: string;
  /** Negative or zero. */
  stickers: number;
  stickersByLine: StickersByLine;
  redemptionReversals: Array<RedemptionRecord & { reversesId: string }>;
  createdAt: string;
}

export type LoyaltyRecord = EarnRecord | ReverseRecord;

export const earnKey = (saleId: string) => `loyalty:earn:${saleId}`;
export const reverseKey = (envelopeOpId: string) => `loyalty:reverse:${envelopeOpId}`;

export async function earnFor(saleId: string): Promise<EarnRecord | null> {
  const row = await db.moduleRecords.get(earnKey(saleId));
  return row?.moduleId === MODULE_ID ? (row.record as EarnRecord) : null;
}

export async function reversalsFor(saleId: string): Promise<ReverseRecord[]> {
  const rows = await db.moduleRecords.where("moduleId").equals(MODULE_ID).toArray();
  return rows
    .map((row) => row.record as LoyaltyRecord)
    .filter((record): record is ReverseRecord => record.kind === "reverse" && record.saleId === saleId);
}

export async function recordsForShift(shiftId: string): Promise<LoyaltyRecord[]> {
  const rows = await db.moduleRecords.where("shiftId").equals(shiftId).toArray();
  return rows.filter((row) => row.moduleId === MODULE_ID).map((row) => row.record as LoyaltyRecord);
}

/**
 * True while the replica cannot yet reflect a record: its envelope is still
 * waiting to sync, or was when the replica was last fetched, or it was made
 * after that fetch began.
 */
function notInReplica(record: LoyaltyRecord, replica: LoyaltyReplica, waiting: ReadonlySet<string>): boolean {
  return (
    waiting.has(record.envelopeOpId) ||
    replica.pendingAtFetch.includes(record.envelopeOpId) ||
    replica.fetchStartedAt === "" ||
    record.createdAt >= replica.fetchStartedAt
  );
}

export interface KnownCard {
  status: "active" | "blocked" | "retired";
  L: number;
  R: number;
  state: CardState;
}

/**
 * A card as this till knows it: the replica's figures plus whatever this till
 * has recorded that the replica cannot have seen yet. Null for a digest that
 * is not a registered card.
 *
 * With one till selling (Clarification Q2), that is the whole truth, which is
 * what makes redeeming offline safe.
 */
export async function knownCard(digest: string): Promise<KnownCard | null> {
  const replica = await loadReplica();
  const base = replica.cards[digest];
  if (!base) return null;

  const [rows, waitingRows] = await Promise.all([
    db.moduleRecords.where("moduleId").equals(MODULE_ID).toArray(),
    db.outbox.filter((row) => LOYALTY_ENVELOPES.includes(row.type)).toArray(),
  ]);
  const waiting = new Set(waitingRows.map((row) => row.opId));

  let L = base.L;
  let R = base.R;
  for (const row of rows) {
    const record = row.record as LoyaltyRecord;
    if (record.digest !== digest || !notInReplica(record, replica, waiting)) continue;
    if (record.kind === "earn") {
      L += record.stickers;
      R += record.redemptions.length;
    } else {
      L += record.stickers;
      R -= record.redemptionReversals.length;
    }
  }

  return { status: base.status, L, R, state: cardState(L, R) };
}
